import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { formatCurrency } from "@/lib/money";
import { PAYMENT_METHOD_LABELS, type PaymentMethod } from "@/lib/paymentMethod";

export interface QuickSaleReceiptPromptModalProps {
  open: boolean;
  invoiceNumber: string;
  totalAmount: string;
  currency: string;
  paymentMethod: PaymentMethod;
  hasPickupItems: boolean;
  pickupReceiptPrinted: boolean;
  pickupReceiptMessage: string | null;
  cashDrawerOpened: boolean;
  cashDrawerMessage: string | null;
  printing: boolean;
  printError: string | null;
  onPrint: () => void;
  onSkip: () => void;
}

export function QuickSaleReceiptPromptModal({
  open,
  invoiceNumber,
  totalAmount,
  currency,
  paymentMethod,
  hasPickupItems,
  pickupReceiptPrinted,
  pickupReceiptMessage,
  cashDrawerOpened,
  cashDrawerMessage,
  printing,
  printError,
  onPrint,
  onSkip,
}: QuickSaleReceiptPromptModalProps) {
  return (
    <Modal
      open={open}
      onClose={printing ? () => {} : onSkip}
      title="Verkauf abgeschlossen"
      description="Zahlung erfolgreich verbucht. Soll der Kassenbon gedruckt werden?"
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="secondary" disabled={printing} onClick={onSkip}>
            Ohne Kassenbon fortfahren
          </Button>
          <Button type="button" loading={printing} onClick={onPrint}>
            Kassenbon drucken
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="rounded-lg border border-separator bg-fill-tertiary p-4 text-sm">
          <p className="font-mono text-xs text-label-secondary">{invoiceNumber}</p>
          <p className="mt-1 tabular-nums">
            {formatCurrency(totalAmount, currency)} – {PAYMENT_METHOD_LABELS[paymentMethod]}
          </p>
        </div>

        {paymentMethod === "cash" ? (
          cashDrawerOpened ? (
            <p className="text-sm text-status-green">Kassenschublade wurde geöffnet.</p>
          ) : cashDrawerMessage ? (
            <p className="text-sm text-status-amber">{cashDrawerMessage}</p>
          ) : null
        ) : null}

        {hasPickupItems ? (
          pickupReceiptPrinted ? (
            <p className="text-sm text-status-green">
              Abholungsbon wurde automatisch gedruckt.
            </p>
          ) : pickupReceiptMessage ? (
            <p className="text-sm text-status-amber">{pickupReceiptMessage}</p>
          ) : (
            <p className="text-sm text-label-secondary">
              Abholungsbon konnte nicht gedruckt werden.
            </p>
          )
        ) : null}

        {printError ? <p className="text-sm text-status-red">{printError}</p> : null}
      </div>
    </Modal>
  );
}
